// Verification Desk — where a clocked visit becomes a billable one.
//
// Reads the visit_review view (visits + clock events + tasks + the agency's
// EVV tolerances, with the exception list computed in SQL) and lets the
// office: watch live late clock-ins/outs, work the exception queue, correct
// a visit with a mandatory reason, attach aggregator reason codes, and
// verify (COMP) or reopen. Verified visits are what billing, payroll and the
// Netsmart transmitter will read; nothing unverified leaves this desk.
import { useCallback, useEffect, useMemo, useState } from 'react';
import { format, addDays, startOfWeek, parseISO } from 'date-fns';
import { useAuth } from '../../hooks/useAuth';
import DataTable from '../../components/DataTable';
import IncidentReportModal from '../../components/quality/IncidentReportModal';
import {
  getVisitReview, verifyVisit, unverifyVisit, updateVisit, getVisitEvents, getEvvSettings, saveEvvSettings, getReasonCodes, geocodeClient, getVisitSignatures,
  EXCEPTION_LABELS, SEVERITY_STYLE, MISSED_REASONS, MISSED_ACTIONS, STATUS_LABEL, SERVICE_COLOR, evvStatusFor
} from '../../lib/scheduling';

const iso = (d) => format(d, 'yyyy-MM-dd');
const t = (s) => s ? format(parseISO(s), 'h:mmaaa').replace(':00', '') : '—';
const dt = (s) => s ? format(parseISO(s), 'EEE d MMM · h:mmaaa') : '—';
const toLocalInput = (s) => s ? format(parseISO(s), "yyyy-MM-dd'T'HH:mm") : '';
const fromLocalInput = (s) => s ? new Date(s).toISOString() : null;
const minute = (s) => s ? Math.floor(new Date(s).getTime() / 60000) : null;
const hrs = (a, b) => (a && b) ? Math.round(((new Date(b) - new Date(a)) / 3600000) * 100) / 100 : null;
const clientName = (r) => `${r.client_last || ''}, ${r.client_first || ''}`.replace(/^, |, $/g, '') + (r.client_ref ? ` · ${r.client_ref}` : '');
const staffName = (r) => r.staff_id ? (r.staff_first || r.staff_last ? `${r.staff_last || ''}, ${r.staff_first || ''}`.replace(/^, |, $/g, '') : r.staff_full) : 'Open';

const field = { width: '100%', padding: '8px 10px', borderRadius: 7, border: '1px solid var(--border)', background: 'var(--bg2)', color: 'var(--text)', fontSize: 13, fontFamily: 'inherit' };
const label = { fontSize: 11, color: 'var(--text3)', marginBottom: 5 };
const overlay = { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1000, display: 'flex', alignItems: 'flex-start', justifyContent: 'center', padding: '32px 16px', overflowY: 'auto' };
const sheet = (w) => ({ background: 'var(--card-bg)', borderRadius: 12, width: '100%', maxWidth: w, boxShadow: '0 20px 60px rgba(0,0,0,0.3)' });
const sheetHead = { padding: '14px 20px', borderBottom: '0.5px solid var(--border)', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 };
const errBox = { background: 'var(--danger-bg)', border: '0.5px solid var(--danger-border)', borderRadius: 7, padding: '10px 14px', margin: '12px 0', fontSize: 12, color: 'var(--danger)' };
const section = { fontSize: 11, fontWeight: 600, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--text3)', margin: '16px 0 8px', paddingBottom: 6, borderBottom: '0.5px solid var(--border)' };

const worst = (codes) => {
  const sev = (codes || []).map(c => EXCEPTION_LABELS[c]?.severity || 'info');
  if (sev.includes('block')) return 'block';
  if (sev.includes('live')) return 'live';
  if (sev.includes('warn')) return 'warn';
  return sev.length ? 'info' : null;
};

const ExceptionChips = ({ codes }) => (
  <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
    {(codes || []).length === 0 && <span className="badge badge-done">Clean</span>}
    {(codes || []).map(c => {
      const meta = EXCEPTION_LABELS[c] || { label: c, severity: 'info' };
      const st = SEVERITY_STYLE[meta.severity];
      return <span key={c} className="badge" style={{ color: st.color, background: st.bg, border: `0.5px solid ${st.border}` }}>{meta.label}</span>;
    })}
  </div>
);

const FILTERS = [
  ['live',     'Live alerts'],
  ['rejected', 'EVV rejected'],
  ['review',   'Needs review'],
  ['ready',    'Ready to verify'],
  ['verified', 'Verified'],
  ['all',      'All'],
];

export default function VerificationDesk() {
  const { profile } = useAuth();
  const canEditSettings = ['admin', 'manager'].includes(profile?.role);
  const [weekStart, setWeekStart] = useState(() => startOfWeek(new Date(), { weekStartsOn: 0 }));
  const [filter, setFilter] = useState('review');
  const [rows, setRows] = useState([]);
  const [codes, setCodes] = useState([]);
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  const [drawer, setDrawer] = useState(null);
  const [reporting, setReporting] = useState(null);   // file an incident from the visit in front of you
  const [form, setForm] = useState(null);
  const [events, setEvents] = useState([]);
  const [signatures, setSignatures] = useState([]);
  const [formError, setFormError] = useState('');
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [sform, setSform] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    const [rv, rc, rs] = await Promise.all([
      getVisitReview(`${iso(weekStart)}T00:00:00`, `${iso(addDays(weekStart, 7))}T00:00:00`),
      getReasonCodes(), getEvvSettings(),
    ]);
    const err = rv.error || rc.error || rs.error;
    if (err) setError(err.message); else setError('');
    setRows(rv.data || []); setCodes(rc.data || []); setSettings(rs.data || null);
    setLoading(false);
  }, [weekStart]);
  useEffect(() => { load(); }, [load]);

  // ── Buckets ─────────────────────────────────────────────────────────────
  const buckets = useMemo(() => {
    const live = rows.filter(r => (r.exceptions || []).some(c => EXCEPTION_LABELS[c]?.severity === 'live'));
    const unverified = rows.filter(r => !r.verified_at && ['completed', 'missed'].includes(r.status));
    // The aggregator's answer reopens a visit even after it was verified.
    const rejected = rows.filter(r => (r.exceptions || []).some(c => c === 'EVV_REJECTED' || c === 'EVV_ERROR'));
    const review = [...unverified.filter(r => (r.exceptions || []).length > 0), ...rejected.filter(r => !!r.verified_at)];
    const ready = unverified.filter(r => (r.exceptions || []).length === 0);
    const verified = rows.filter(r => !!r.verified_at);
    return { live, rejected, review, ready, verified, all: rows };
  }, [rows]);
  const shown = buckets[filter] || rows;
  const verifiedUnits = buckets.verified.reduce((s, r) => s + Number(r.billable_units || 0), 0);

  // ── Drawer ──────────────────────────────────────────────────────────────
  const openRow = async (r) => {
    setDrawer(r);
    setForm({
      actual_start: toLocalInput(r.actual_start), actual_end: toLocalInput(r.actual_end),
      billable_start: toLocalInput(r.billable_start || r.actual_start), billable_end: toLocalInput(r.billable_end || r.actual_end),
      status: r.status, missed_reason_code: r.missed_reason_code || '', missed_action_code: r.missed_action_code || '',
      reason_codes: r.reason_codes || [], client_signature_note: r.client_signature_note || '',
      modification_reason: '', verify_note: '',
    });
    setFormError('');
    setSignatures([]);
    const [{ data }, sig] = await Promise.all([getVisitEvents(r.id), getVisitSignatures(r.id)]);
    setEvents(data || []);
    setSignatures(sig.data || []);
  };
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const toggleCode = (c) => setForm(f => ({ ...f, reason_codes: f.reason_codes.includes(c) ? f.reason_codes.filter(x => x !== c) : [...f.reason_codes, c] }));

  const buildPatch = () => {
    const patch = {};
    const cmp = (k, val) => { if ((val || null) !== (drawer[k] || null)) patch[k] = val; };
    // datetime-local inputs carry minutes only; compare at minute precision so an
    // untouched field (whose stored value has seconds) doesn't read as a change.
    const cmpTime = (k, val) => { if (minute(val) !== minute(drawer[k])) patch[k] = val; };
    cmpTime('actual_start', fromLocalInput(form.actual_start));
    cmpTime('actual_end', fromLocalInput(form.actual_end));
    // The billable window defaults from the clocked times in the form, so an
    // untouched default is not a correction — compare against that fallback.
    const cmpBill = (k, fallbackKey, val) => { if (minute(val) !== minute(drawer[k] || drawer[fallbackKey])) patch[k] = val; };
    cmpBill('billable_start', 'actual_start', fromLocalInput(form.billable_start));
    cmpBill('billable_end', 'actual_end', fromLocalInput(form.billable_end));
    if (form.status !== drawer.status) {
      patch.status = form.status;
      patch.evv_status = evvStatusFor(form.status);
    }
    if (form.status === 'missed') { cmp('missed_reason_code', form.missed_reason_code || null); cmp('missed_action_code', form.missed_action_code || null); }
    if (JSON.stringify(form.reason_codes) !== JSON.stringify(drawer.reason_codes || [])) patch.reason_codes = form.reason_codes;
    cmp('client_signature_note', form.client_signature_note || null);
    return patch;
  };

  const saveChanges = async () => {
    const patch = buildPatch();
    if (Object.keys(patch).length === 0) return setFormError('Nothing changed.');
    if (drawer.evv_required && !form.modification_reason.trim()) return setFormError('Every correction on an EVV line needs a reason. It is written to the audit trail with your name.');
    if (drawer.evv_required && form.status === 'missed' && (!form.missed_reason_code || !form.missed_action_code)) return setFormError('A missed visit on an EVV line needs a reason code and an action taken.');
    if (drawer.verified_at) return setFormError('This visit is verified. Reopen it first, then correct it.');
    setBusy(true); setFormError('');
    const { error: err } = await updateVisit(drawer.id, { ...patch, modification_reason: form.modification_reason.trim() || null });
    setBusy(false);
    if (err) return setFormError(err.message);
    setDrawer(null); load();
  };

  const doVerify = async (r, opts = {}) => {
    setBusy(true); setFormError('');
    const { error: err } = await verifyVisit(r.id, opts);
    setBusy(false);
    if (err) { setFormError(err.message); setError(drawer ? '' : err.message); return false; }
    return true;
  };
  const verifyFromDrawer = async () => {
    const patch = buildPatch();
    if (drawer.evv_required && Object.keys(patch).length && !form.modification_reason.trim()) return setFormError('Save your corrections with a reason first, or clear them.');
    if (Object.keys(patch).length) {
      const { error: err } = await updateVisit(drawer.id, { ...patch, modification_reason: form.modification_reason.trim() || null });
      if (err) return setFormError(err.message);
    }
    // Only send a billable window the office actually changed; otherwise the
    // function falls back to the clocked times with full precision.
    const bs = fromLocalInput(form.billable_start), be = fromLocalInput(form.billable_end);
    const ok = await doVerify(drawer, {
      billableStart: minute(bs) !== minute(drawer.billable_start || drawer.actual_start) ? bs : null,
      billableEnd:   minute(be) !== minute(drawer.billable_end   || drawer.actual_end)   ? be : null,
      note: form.verify_note || null,
    });
    if (ok) { setDrawer(null); load(); }
  };
  const verifyAllReady = async () => {
    if (!buckets.ready.length) return;
    if (!window.confirm(`Verify ${buckets.ready.length} clean visit${buckets.ready.length === 1 ? '' : 's'} using their clocked times?`)) return;
    setBusy(true);
    let failed = 0;
    for (const r of buckets.ready) { const { error: err } = await verifyVisit(r.id, {}); if (err) failed += 1; }
    setBusy(false);
    if (failed) setError(`${failed} visit${failed === 1 ? '' : 's'} could not be verified — open them to see why.`);
    load();
  };
  const reopen = async () => {
    const reason = window.prompt('Why are you reopening this verified visit?');
    if (!reason || !reason.trim()) return;
    setBusy(true);
    const { error: err } = await unverifyVisit(drawer.id, reason.trim());
    setBusy(false);
    if (err) return setFormError(err.message);
    setDrawer(null); load();
  };

  const openSettings = () => { setSform({ ...settings }); setSettingsOpen(true); };
  const saveSettings = async () => {
    setBusy(true);
    const { agency_id, updated_by, updated_at, ...data } = sform;
    ['early_clock_in_minutes', 'late_alert_minutes', 'start_variance_tolerance_minutes', 'duration_tolerance_minutes', 'geofence_radius_m'].forEach(k => { data[k] = Number(data[k]); });
    const { error: err } = await saveEvvSettings(agency_id, { ...data, updated_by: profile?.id || null });
    setBusy(false);
    if (err) return setError(err.message);
    setSettingsOpen(false); load();
  };

  // ── Table ───────────────────────────────────────────────────────────────
  const columns = useMemo(() => [
    { id: 'when', header: 'Scheduled', size: 170, accessorKey: 'scheduled_start', meta: { mobile: 'primary' },
      cell: ({ row }) => <span>{format(parseISO(row.original.scheduled_start), 'EEE d MMM')} <span style={{ color: 'var(--text3)' }}>{t(row.original.scheduled_start)}–{t(row.original.scheduled_end)}</span></span> },
    { id: 'client', header: 'Client', size: 190, accessorFn: clientName, cell: ({ row }) => <span style={{ fontWeight: 500 }}>{clientName(row.original)}</span> },
    { id: 'staff', header: 'Caregiver', size: 160, accessorFn: staffName, cell: ({ row }) => staffName(row.original) },
    { id: 'service', header: 'Service', size: 150, accessorKey: 'service_name',
      cell: ({ row }) => <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}><span style={{ width: 8, height: 8, borderRadius: 2, background: SERVICE_COLOR[row.original.service_color] || SERVICE_COLOR.other }} />{row.original.service_name}</span> },
    { id: 'clock', header: 'Clocked', size: 150, enableSorting: false,
      cell: ({ row }) => { const r = row.original; return r.actual_start ? <span>{t(r.actual_start)}{r.gps_in ? '' : ' ⚐'} – {r.actual_end ? `${t(r.actual_end)}${r.gps_out ? '' : ' ⚐'}` : '…'}</span> : <span style={{ color: 'var(--text3)' }}>—</span>; } },
    { id: 'hours', header: 'Hours', size: 90, meta: { align: 'right' }, accessorFn: r => hrs(r.actual_start, r.actual_end) ?? -1,
      cell: ({ row }) => { const r = row.original; const a = hrs(r.actual_start, r.actual_end); const s = hrs(r.scheduled_start, r.scheduled_end); return <span style={{ fontVariantNumeric: 'tabular-nums' }}>{a ?? '—'}<span style={{ color: 'var(--text3)' }}> / {s}</span></span>; } },
    { id: 'exceptions', header: 'Exceptions', size: 260, enableSorting: false, cell: ({ row }) => <ExceptionChips codes={row.original.exceptions} /> },
    { id: 'evv', header: 'EVV', size: 120, accessorKey: 'evv_status',
      cell: ({ row }) => { const r = row.original; return r.verified_at ? <span className="badge badge-done">Verified · {r.evv_status}</span> : <span className="badge badge-low">{STATUS_LABEL[r.status]} · {r.evv_status}</span>; } },
    { id: 'actions', header: '', size: 110, enableSorting: false, meta: { mobile: 'hidden' },
      cell: ({ row }) => { const r = row.original; const ready = !r.verified_at && ['completed', 'missed'].includes(r.status) && (r.exceptions || []).length === 0;
        return <div style={{ display: 'flex', justifyContent: 'flex-end' }} onClick={e => e.stopPropagation()}>
          {ready ? <button className="btn btn-primary" style={{ padding: '4px 10px', fontSize: 12 }} disabled={busy} onClick={async () => { if (await doVerify(r)) load(); }}>Verify</button>
                 : <button className="btn" style={{ padding: '4px 10px', fontSize: 12 }} onClick={() => openRow(r)}>Review</button>}
        </div>; } },
  // eslint-disable-next-line react-hooks/exhaustive-deps
  ], [busy]);

  return (
    <div>
      <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between', gap: 10, marginBottom: 12 }}>
        <div style={{ fontSize: 12, color: 'var(--text3)', maxWidth: 560, lineHeight: 1.5 }}>
          Every clocked visit stops here before billing, payroll or the state feed. Clean visits verify in one click;
          anything with an exception is corrected with a reason, then verified. ⚐ marks a clock event without GPS.
        </div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          {canEditSettings && settings && <button className="btn" onClick={openSettings}>⚙ Tolerances</button>}
          <button className="btn btn-primary" onClick={verifyAllReady} disabled={busy || buckets.ready.length === 0}>✓ Verify all ready ({buckets.ready.length})</button>
        </div>
      </div>

      {error && <div style={errBox}>⚠ {error}</div>}

      <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 8, marginBottom: 12 }}>
        <div style={{ display: 'flex', border: '1px solid var(--border)', borderRadius: 8, overflow: 'hidden' }}>
          {FILTERS.map(([k, l]) => (
            <button key={k} onClick={() => setFilter(k)} className="btn" style={{ borderRadius: 0, border: 'none', background: filter === k ? 'var(--accent)' : 'transparent', color: filter === k ? 'var(--white)' : 'var(--text2)' }}>
              {l} <span style={{ opacity: 0.75 }}>({buckets[k].length})</span>
            </button>
          ))}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginLeft: 'auto' }}>
          <button className="btn" onClick={() => setWeekStart(d => addDays(d, -7))}>← Prev</button>
          <button className="btn" onClick={() => setWeekStart(startOfWeek(new Date(), { weekStartsOn: 0 }))}>This week</button>
          <button className="btn" onClick={() => setWeekStart(d => addDays(d, 7))}>Next →</button>
          <span style={{ fontSize: 13, fontWeight: 600, marginLeft: 6 }}>{format(weekStart, 'd MMM')} – {format(addDays(weekStart, 6), 'd MMM yyyy')}</span>
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 1, background: 'var(--border)', border: '1px solid var(--border)', borderRadius: 8, overflow: 'hidden', marginBottom: 14 }}>
        {[['Live alerts', buckets.live.length, buckets.live.length ? 'var(--danger)' : 'var(--text)'], ['Needs review', buckets.review.length, buckets.review.length ? 'var(--warn)' : 'var(--text)'], ['Ready', buckets.ready.length, 'var(--text)'], ['Verified', buckets.verified.length, 'var(--success)'], ['Verified units', verifiedUnits, 'var(--text)']].map(([l, n, c]) => (
          <div key={l} style={{ background: 'var(--card-bg)', padding: '10px 14px' }}>
            <div style={{ fontSize: 18, fontWeight: 600, color: c, fontVariantNumeric: 'tabular-nums' }}>{n}</div>
            <div style={{ fontSize: 11, color: 'var(--text3)' }}>{l}</div>
          </div>
        ))}
      </div>

      <div className="card">
        <DataTable tableId="verification-desk" columns={columns} data={shown} getRowId={r => r.id} onRowClick={openRow} loading={loading}
          emptyMessage={filter === 'review' ? 'Nothing needs review this week.' : filter === 'live' ? 'No late clock-ins or clock-outs right now.' : 'No visits in this bucket for the week.'}
          rowStyle={r => worst(r.exceptions) === 'block' || worst(r.exceptions) === 'live' ? { background: 'var(--danger-bg)' } : undefined} />
      </div>

      {/* Review drawer */}
      {drawer && form && (
        <div style={overlay}>
          <div style={sheet(640)}>
            <div style={sheetHead}>
              <div>
                <div style={{ fontSize: 15, fontWeight: 700 }}>{clientName(drawer)}</div>
                <div style={{ fontSize: 11.5, color: 'var(--text3)', fontFamily: 'var(--mono)' }}>{drawer.evv_visit_id} · {drawer.service_name} · {staffName(drawer)} · {format(parseISO(drawer.scheduled_start), 'EEE d MMM yyyy')}</div>
              </div>
              <button onClick={() => setDrawer(null)} style={{ background: 'none', border: 'none', fontSize: 22, cursor: 'pointer', color: 'var(--text3)', lineHeight: 1 }} aria-label="Close">×</button>
            </div>
            <div style={{ padding: '4px 20px 20px' }}>
              {formError && <div style={errBox}>{formError}</div>}
              <div style={{ marginTop: 12 }}><ExceptionChips codes={drawer.exceptions} /></div>
              {drawer.evv_response_status && ['rejected', 'error'].includes(drawer.evv_response_status) && (
                <div style={{ marginTop: 10, background: 'var(--danger-bg)', border: '0.5px solid var(--danger-border)', borderRadius: 7, padding: '9px 12px', fontSize: 12, color: 'var(--danger)' }}>
                  <b>{drawer.evv_response_status === 'rejected' ? 'Rejected by the aggregator' : 'Transmission error'}</b>
                  {drawer.evv_response_code ? ` · ${drawer.evv_response_code}` : ''}{drawer.evv_response_message ? ` — ${drawer.evv_response_message}` : ''}
                  <div style={{ color: 'var(--text3)', marginTop: 3 }}>{drawer.evv_file_name || ''}{drawer.evv_answered_at ? ` · ${dt(drawer.evv_answered_at)}` : ''} · correct the visit and it goes out with the next batch.</div>
                </div>
              )}
              {drawer.verified_at && <div style={{ marginTop: 8, fontSize: 12, color: 'var(--success)' }}>Verified by {drawer.verified_by_name || 'office'} on {dt(drawer.verified_at)} · {drawer.billable_units} units</div>}

              <div style={section}>Clock times (what happened)</div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <div><div style={label}>Clock-in {drawer.gps_in ? `· GPS${drawer.start_distance_m != null ? ` · ${Math.round(drawer.start_distance_m)} m from home` : ''}` : drawer.actual_start ? '· no GPS' : ''}</div><input type="datetime-local" style={field} value={form.actual_start} onChange={e => set('actual_start', e.target.value)} disabled={!!drawer.verified_at} /></div>
                <div><div style={label}>Clock-out {drawer.gps_out ? `· GPS${drawer.end_distance_m != null ? ` · ${Math.round(drawer.end_distance_m)} m from home` : ''}` : drawer.actual_end ? '· no GPS' : ''}</div><input type="datetime-local" style={field} value={form.actual_end} onChange={e => set('actual_end', e.target.value)} disabled={!!drawer.verified_at} /></div>
              </div>
              <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 6 }}>Scheduled {t(drawer.scheduled_start)}–{t(drawer.scheduled_end)}. Changing a clock time is an office edit and is logged as such.</div>

              <div style={section}>Billable window (what gets billed)</div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <div><div style={label}>Billable start</div><input type="datetime-local" style={field} value={form.billable_start} onChange={e => set('billable_start', e.target.value)} disabled={!!drawer.verified_at} /></div>
                <div><div style={label}>Billable end</div><input type="datetime-local" style={field} value={form.billable_end} onChange={e => set('billable_end', e.target.value)} disabled={!!drawer.verified_at} /></div>
              </div>
              <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 6 }}>
                {hrs(fromLocalInput(form.billable_start), fromLocalInput(form.billable_end)) ?? '—'} billable hours · {drawer.unit_basis === 'hour' ? (drawer.round_units_to_whole ? 'rounded to whole hours' : 'to 2 decimals') : drawer.unit_basis === 'quarter_hour' ? '15-minute units' : 'per visit'}
              </div>

              <div style={section}>Status</div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12 }}>
                <div>
                  <div style={label}>Status</div>
                  <select style={field} value={form.status} onChange={e => set('status', e.target.value)} disabled={!!drawer.verified_at}>
                    {['scheduled', 'in_progress', 'completed', 'incomplete', 'missed', 'cancelled'].map(s => <option key={s} value={s}>{STATUS_LABEL[s]}</option>)}
                  </select>
                </div>
                {form.status === 'missed' && (<>
                  <div><div style={label}>Missed reason</div>
                    <select style={field} value={form.missed_reason_code} onChange={e => set('missed_reason_code', e.target.value)} disabled={!!drawer.verified_at}><option value="">Choose…</option>{MISSED_REASONS.map(r => <option key={r.code} value={r.code}>{r.label} ({r.code})</option>)}</select></div>
                  <div><div style={label}>Action taken</div>
                    <select style={field} value={form.missed_action_code} onChange={e => set('missed_action_code', e.target.value)} disabled={!!drawer.verified_at}><option value="">Choose…</option>{MISSED_ACTIONS.map(r => <option key={r.code} value={r.code}>{r.label} ({r.code})</option>)}</select></div>
                </>)}
              </div>
              {settings?.require_client_signature && !drawer.client_signed_at && (
                <div style={{ marginTop: 12 }}><div style={label}>No-signature reason</div><input style={field} value={form.client_signature_note} onChange={e => set('client_signature_note', e.target.value)} disabled={!!drawer.verified_at} /></div>
              )}

              <div style={section}>Aggregator reason codes</div>
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                {codes.map(c => { const on = form.reason_codes.includes(c.code); return (
                  <button key={c.code} type="button" className="btn" onClick={() => toggleCode(c.code)} disabled={!!drawer.verified_at} title={c.category}
                    style={{ padding: '4px 10px', fontSize: 12, background: on ? 'var(--accent)' : 'var(--bg2)', color: on ? 'var(--white)' : 'var(--text2)', border: on ? 'none' : '1px solid var(--border)' }}>
                    {c.category} {c.code}
                  </button>); })}
              </div>
              <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 6 }}>Attach only what the transmitter should pre-clear. Labels are working descriptions until confirmed against the Netsmart dictionary.</div>

              {!drawer.verified_at && (<>
                <div style={section}>Reason for corrections{drawer.evv_required ? '' : ' (optional — EVV is off for this line)'}</div>
                <input style={field} value={form.modification_reason} onChange={e => set('modification_reason', e.target.value)} placeholder={drawer.evv_required ? 'Required if you changed anything above' : 'Optional on this line'} />
                <div style={{ marginTop: 10 }}><div style={label}>Verification note (optional)</div><input style={field} value={form.verify_note} onChange={e => set('verify_note', e.target.value)} /></div>
              </>)}

              {(signatures.length > 0 || drawer.client_signature_note) && (<>
                <div style={section}>Signatures</div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 12 }}>
                  {signatures.map(sg => (
                    <div key={sg.id} style={{ border: '0.5px solid var(--border)', borderRadius: 8, padding: 10, background: 'var(--bg2)' }}>
                      <svg viewBox={`0 0 1000 ${Math.round(1000 / (Number(sg.aspect_ratio) || 2.5))}`} style={{ width: '100%', height: 'auto', display: 'block', background: 'var(--card-bg)', borderRadius: 6 }} role="img" aria-label={`${sg.kind} signature`}>
                        <path d={sg.svg_path} fill="none" stroke="var(--text)" strokeWidth="6" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                      <div style={{ fontSize: 12, marginTop: 6 }}><strong>{sg.kind === 'caregiver' ? 'Caregiver' : 'Client / guardian'}</strong> · {sg.signer_name}{sg.relationship && sg.relationship !== 'self' ? ` (${sg.relationship})` : ''}</div>
                      <div style={{ fontSize: 11, color: 'var(--text3)' }}>{dt(sg.signed_at)}</div>
                    </div>
                  ))}
                  {drawer.client_signature_note && !signatures.some(sg => sg.kind === 'client') && (
                    <div style={{ border: '0.5px solid var(--warn-border)', borderRadius: 8, padding: 10, background: 'var(--warn-bg)', fontSize: 12, color: 'var(--warn)' }}>
                      No client signature: {drawer.client_signature_note}
                    </div>
                  )}
                </div>
              </>)}

              <div style={section}>Incident</div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                <button className="btn btn-outline btn-sm" onClick={() => setReporting(drawer)}>Report an incident from this visit</button>
                <span style={{ fontSize: 11, color: 'var(--text3)' }}>A fall, an injury, a medication error — filed against the client and the caregiver, with the 24-hour clock started.</span>
              </div>

              <div style={section}>Audit trail</div>
              {events.length === 0 ? <div style={{ fontSize: 12, color: 'var(--text3)' }}>No events yet.</div> : events.map(e => (
                <div key={e.id} style={{ fontSize: 12, color: 'var(--text2)', padding: '5px 0', borderBottom: '0.5px solid var(--border)' }}>
                  <span style={{ fontFamily: 'var(--mono)', fontSize: 11, color: 'var(--text3)' }}>{format(parseISO(e.occurred_at), 'd MMM HH:mm')}</span>
                  {' '}<strong>{e.event_type.replace('_', ' ')}</strong>{e.method && e.method !== 'system' ? ` · ${e.method}` : ''}{e.actor?.full_name ? ` · ${e.actor.full_name}` : ''}{e.reason_code ? ` · ${e.reason_code}` : ''}{e.note ? ` · ${e.note}` : ''}
                  {e.changes && <div style={{ fontSize: 11, color: 'var(--text3)' }}>{Object.entries(e.changes).filter(([k]) => k !== 'modification_reason').map(([k, c]) => `${k}: ${c.from ?? '—'} → ${c.to ?? '—'}`).join(' · ')}</div>}
                </div>
              ))}

              {formError && <div style={errBox}>{formError}</div>}
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 18, flexWrap: 'wrap' }}>
                <button className="btn" onClick={() => setDrawer(null)}>Close</button>
                {drawer.verified_at
                  ? <button className="btn" style={{ color: 'var(--danger)' }} onClick={reopen} disabled={busy}>Reopen</button>
                  : (<>
                    <button className="btn" onClick={saveChanges} disabled={busy}>Save corrections</button>
                    {['completed', 'missed'].includes(form.status) && <button className="btn btn-primary" onClick={verifyFromDrawer} disabled={busy}>{busy ? 'Working…' : '✓ Verify'}</button>}
                  </>)}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Tolerances */}
      {settingsOpen && sform && (
        <div style={overlay}>
          <div style={sheet(520)}>
            <div style={sheetHead}><div style={{ fontSize: 15, fontWeight: 700 }}>EVV tolerances</div>
              <button onClick={() => setSettingsOpen(false)} style={{ background: 'none', border: 'none', fontSize: 22, cursor: 'pointer', color: 'var(--text3)', lineHeight: 1 }} aria-label="Close">×</button></div>
            <div style={{ padding: '6px 20px 20px' }}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginTop: 12 }}>
                {[['early_clock_in_minutes', 'Clock-in opens (minutes before start)'], ['late_alert_minutes', 'Late alert after (minutes)'], ['start_variance_tolerance_minutes', 'Start-time variance tolerance (min)'], ['duration_tolerance_minutes', 'Duration variance tolerance (min)'], ['geofence_radius_m', 'Geofence radius (metres)']].map(([k, l]) => (
                  <div key={k}><div style={label}>{l}</div><input type="number" min="0" style={field} value={sform[k] ?? ''} onChange={e => setSform(f => ({ ...f, [k]: e.target.value }))} /></div>
                ))}
              </div>
              <div style={{ display: 'grid', gap: 8, marginTop: 14 }}>
                {[['require_gps', 'Flag clock events without GPS'], ['require_client_signature', 'Require a client signature (or a no-signature reason)'], ['auto_verify_clean', 'Auto-verify exception-free visits nightly (not yet active)']].map(([k, l]) => (
                  <label key={k} style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: 12, color: 'var(--text2)', cursor: 'pointer' }}>
                    <input type="checkbox" checked={!!sform[k]} onChange={e => setSform(f => ({ ...f, [k]: e.target.checked }))} /> {l}
                  </label>
                ))}
              </div>
              <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 10 }}>Client addresses are geocoded nightly (US Census geocoder); a clock event farther than the radius from the home is flagged "Outside geofence", never rejected.</div>
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, marginTop: 18, flexWrap: 'wrap' }}>
                <button className="btn" disabled={busy} onClick={async () => {
                  setBusy(true);
                  const { data, error: err } = await geocodeClient();
                  setBusy(false);
                  window.alert(err || data?.error ? `Geocoding failed: ${err?.message || data?.error}` : `Checked ${data.checked} clients, ${data.due} needed geocoding, ${data.geocoded} placed on the map.`);
                }}>📍 Geocode client addresses now</button>
                <div style={{ display: 'flex', gap: 8 }}>
                  <button className="btn" onClick={() => setSettingsOpen(false)}>Cancel</button>
                  <button className="btn btn-primary" onClick={saveSettings} disabled={busy}>Save</button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {reporting && (
        <IncidentReportModal
          client={{ id: reporting.client_id, client_id: reporting.client_ref, first_name: reporting.client_first, last_name: reporting.client_last }}
          staff={reporting.staff_id ? { id: reporting.staff_id, first_name: reporting.staff_first, last_name: reporting.staff_last } : null}
          subject="client"
          source="visit"
          onClose={() => setReporting(null)}
        />
      )}
    </div>
  );
}
